// Content Governance - Approved Product Content
// This file contains all approved product names and descriptions.
// DO NOT add technical specifications (resolution, lumens, etc.) without explicit approval.
// Content guardrails prevent rendering of unapproved spec-like fields.

export const CONTENT_VERSION = '1.0.0';

// Forbidden spec patterns - will trigger warning if detected
export const FORBIDDEN_SPEC_PATTERNS = [
  /\d+\s*(?:lux|lumen|cd)/i,
  /\d+\s*(?:MP|megapixel)/i,
  /\d{3,4}\s*x\s*\d{3,4}/i, // Resolution patterns
  /\d+\s*(?:fps|hz)/i,
  /\d+\s*(?:mm|cm|inch)/i,
  /\d+\s*(?:kg|lbs|pounds)/i,
  /\d+\s*(?:watts?|W)/i,
];

// Validate content doesn't contain forbidden specs
export const validateContent = (text) => {
  if (!text || typeof text !== 'string') return { valid: true, warnings: [] };
  
  const warnings = [];
  FORBIDDEN_SPEC_PATTERNS.forEach((pattern, index) => {
    if (pattern.test(text)) {
      warnings.push(`Potential spec detected (pattern ${index + 1}): "${text.substring(0, 50)}..."`);
    }
  });
  
  return {
    valid: warnings.length === 0,
    warnings
  };
};

// Approved product categories and items
export const approvedProducts = {
  cameraProcessors: {
    categoryName: 'Camera Processors',
    categoryDescription: 'Advanced image processing units for endoscopic visualization systems.',
    items: [
      { id: 'cp-1', name: 'XION Camera Processor', description: 'Central processing unit for endoscopic camera systems.' },
      { id: 'cp-2', name: 'HD Processing Unit', description: 'High-definition signal processor for medical imaging.' },
      { id: 'cp-3', name: 'Spectar Processor', description: 'Next-generation image processor for the Spectar series.' },
    ]
  },
  cameraHeads: {
    categoryName: 'Camera Heads',
    categoryDescription: 'Precision camera heads designed for various endoscopic applications.',
    items: [
      { id: 'ch-1', name: 'EndoCAM HD', description: 'Compact camera head for rigid and flexible endoscopy.' },
      { id: 'ch-2', name: 'EndoCAM 4K', description: 'Ultra-high definition camera head for surgical applications.' },
      { id: 'ch-3', name: 'MiniCAM', description: 'Lightweight camera head for office-based procedures.' },
    ]
  },
  illumination: {
    categoryName: 'Illumination',
    categoryDescription: 'Professional lighting solutions for optimal visualization.',
    items: [
      { id: 'il-1', name: 'LED Light Source', description: 'Energy-efficient LED illumination for endoscopy.' },
      { id: 'il-2', name: 'Cold Light Fountain', description: 'Traditional cold light illumination system.' },
      { id: 'il-3', name: 'Portable Light Unit', description: 'Mobile lighting solution for flexible setups.' },
    ]
  },
  flexibleEndoscopes: {
    categoryName: 'Flexible Endoscopes',
    categoryDescription: 'Versatile flexible endoscopes for various diagnostic applications.',
    items: [
      { id: 'fe-1', name: 'Rhinolaryngoscope', description: 'Flexible scope for ENT examination.' },
      { id: 'fe-2', name: 'Bronchoscope', description: 'Flexible scope for airway examination.' },
      { id: 'fe-3', name: 'Nasopharyngoscope', description: 'Slim flexible scope for nasal passages.' },
    ]
  },
  rigidEndoscopes: {
    categoryName: 'Rigid Endoscopes',
    categoryDescription: 'High-quality rigid endoscopes for surgical and diagnostic use.',
    items: [
      { id: 're-1', name: 'Sinuscope', description: 'Rigid scope for sinus examination and surgery.' },
      { id: 're-2', name: 'Laryngoscope', description: 'Rigid scope for laryngeal examination.' },
      { id: 're-3', name: 'Otoscope', description: 'Rigid scope for ear examination.' },
    ]
  },
  instruments: {
    categoryName: 'Instruments',
    categoryDescription: 'Specialized surgical and diagnostic instruments.',
    items: [
      { id: 'in-1', name: 'Biopsy Forceps', description: 'Precision forceps for tissue sampling.' },
      { id: 'in-2', name: 'Grasping Forceps', description: 'Instruments for tissue manipulation.' },
      { id: 'in-3', name: 'Scissors', description: 'Endoscopic cutting instruments.' },
    ]
  },
  equipmentTrolleys: {
    categoryName: 'Equipment Trolleys & Support Systems',
    categoryDescription: 'Mobile workstations and mounting solutions for endoscopy equipment.',
    items: [
      { id: 'et-1', name: 'EndoTROLLEY', description: 'Mobile equipment cart for endoscopy systems.' },
      { id: 'et-2', name: 'Ceiling Mount System', description: 'Ceiling-mounted equipment support.' },
      { id: 'et-3', name: 'Wall Mount Arm', description: 'Space-saving wall-mounted equipment arm.' },
    ]
  },
  monitors: {
    categoryName: 'Monitors',
    categoryDescription: 'Medical-grade displays for endoscopic visualization.',
    items: [
      { id: 'mo-1', name: 'HD Medical Monitor', description: 'High-definition display for medical imaging.' },
      { id: 'mo-2', name: '4K Surgical Display', description: 'Ultra-high definition monitor for surgical suites.' },
      { id: 'mo-3', name: 'Portable Monitor', description: 'Compact display for mobile applications.' },
    ]
  },
  computerPanelPC: {
    categoryName: 'Computer / Panel PC',
    categoryDescription: 'Medical computing solutions for documentation and system control.',
    items: [
      { id: 'pc-1', name: 'Medical Panel PC', description: 'Touch-screen computer for medical environments.' },
      { id: 'pc-2', name: 'EndoPC', description: 'Integrated computing solution for endoscopy.' },
    ]
  },
  software: {
    categoryName: 'Software',
    categoryDescription: 'Documentation and analysis software solutions.',
    items: [
      { id: 'sw-1', name: 'DiVAS 2.8', description: 'Digital video archiving and documentation software.' },
      { id: 'sw-2', name: 'EndoDOC', description: 'Patient documentation and reporting software.' },
    ]
  }
};

// Approved system solutions
export const approvedSystems = {
  laryngoStroboscopy: {
    name: 'Laryngo-stroboscopy and Phoniatrics',
    description: 'Comprehensive solutions for voice and laryngeal examination.',
    namedSystems: ['EndoSTROBE Spectar'],
    benefits: [
      'Advanced stroboscopic imaging for voice analysis',
      'Integrated documentation capabilities',
      'Suitable for clinical and research applications',
      'Ergonomic design for extended procedures'
    ]
  },
  surgery: {
    name: 'Surgery',
    description: 'Operating room solutions for minimally invasive procedures.',
    namedSystems: ['EndoSURGERY Spectar', 'EndoSURGERY 3D Spectar'],
    benefits: [
      'High-definition visualization for surgical precision',
      'Sterile workflow integration',
      'Compatible with existing OR infrastructure',
      'Comprehensive documentation features'
    ]
  },
  swallowingDiagnostics: {
    name: 'Swallowing Diagnostics',
    description: 'Specialized systems for FEES and dysphagia assessment.',
    namedSystems: ['EndoFLEX Spectar'],
    benefits: [
      'Designed for FEES procedures',
      'Portable and bedside-capable',
      'Integrated recording and analysis',
      'Patient-friendly examination experience'
    ]
  },
  officeEndoscopy: {
    name: 'Office Endoscopy',
    description: 'Compact solutions for outpatient clinics and private practices.',
    namedSystems: ['EndoOFFICE Spectar', 'EndoOFFICE Veezar'],
    benefits: [
      'Space-efficient design for office environments',
      'Quick setup and easy operation',
      'Cost-effective for private practice',
      'All-in-one workstation options'
    ]
  }
};

// Approved trust signals - verified claims only
export const trustSignals = {
  madeInGermany: 'Made in Germany',
  since1991: 'Endoscopy systems since 1991',
  internationalDistribution: 'International distribution network',
  clinicalCollaboration: 'Close collaboration with leading hospitals and medical institutions',
  qualityStandards: 'Rigorous quality standards and precision manufacturing'
};

// FAQ content for Contact PH page (for schema)
export const contactFAQs = [
  {
    question: 'Who is the authorized XION distributor in the Philippines?',
    answer: 'Fortschritt Medical Supplies Incorporated (FMSInc.) is the exclusive authorized distributor of XION Medical products in the Philippines. Visit www.fmsmedequip.com for more information.'
  },
  {
    question: 'How do I request a product quotation?',
    answer: 'You can request a quotation by submitting an inquiry through our contact form or by visiting FMSInc. directly at www.fmsmedequip.com. Our distributor will respond within 1-2 business days.'
  },
  {
    question: 'How can I schedule a product demonstration?',
    answer: 'Product demonstrations can be arranged through FMSInc. Submit a demo request through our contact form or contact them directly to schedule a demonstration at your facility.'
  },
  {
    question: 'What is the typical response time for inquiries?',
    answer: 'FMSInc. typically responds to inquiries within 1-2 business days. For urgent matters, we recommend contacting them directly through their website.'
  },
  {
    question: 'Who handles warranty and service in the Philippines?',
    answer: 'All warranty claims, repairs, and technical support for XION products in the Philippines are handled by FMSInc., our exclusive authorized distributor.'
  }
];

export default {
  CONTENT_VERSION,
  approvedProducts,
  approvedSystems,
  trustSignals,
  contactFAQs,
  validateContent,
  FORBIDDEN_SPEC_PATTERNS
};
